import React, { useEffect, useState } from "react";
import { Badge, Mono, CopyBtn } from "@/components/primitives";
import { formatLovelace, truncMid } from "@/lib/format";
import { API, http } from "@/lib/constants";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const lovelaceToAda = (v) => (v ? Number(v) / 1e6 : 0);

// ---------- historical stake trend (real epoch_stake snapshots, not mocked) ----------
const StakeTrendChart = ({ stakeAddress }) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!stakeAddress) return undefined;
    let cancelled = false;
    http
      .get(`${API}/accounts/${stakeAddress}/stake_history`)
      .then((r) => { if (!cancelled) setHistory(r.data); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [stakeAddress]);

  if (!history) {
    return (
      <div className="h-[120px] flex items-center justify-center text-[11px] text-muted-foreground mono">
        Loading trend…
      </div>
    );
  }
  if (history.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center text-[11px] text-muted-foreground mono">
        No stake history yet
      </div>
    );
  }
  const chartData = history.map((h) => ({ epoch: h.epoch_no, ada: lovelaceToAda(h.amount) }));
  const isFlat = chartData.length > 1 && chartData.every((d) => d.ada === chartData[0].ada);
  return (
    <div data-testid="stake-trend-chart">
      <div className="h-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="stakeTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(264 100% 68%)" stopOpacity={0.45} />
                <stop offset="100%" stopColor="hsl(264 100% 68%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="epoch" hide />
            <YAxis hide domain={["auto", "auto"]} />
            <Tooltip
              contentStyle={{
                background: "#12081f",
                border: "1px solid rgba(157,92,255,0.35)",
                borderRadius: 8,
                fontSize: 11,
              }}
              labelFormatter={(v) => `Epoch ${v}`}
              formatter={(v) => [
                `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })} ₳`,
                "Stake",
              ]}
            />
            <Area
              type="monotone"
              dataKey="ada"
              stroke="hsl(264 100% 68%)"
              strokeWidth={2}
              fill="url(#stakeTrendFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {isFlat ? (
        <div data-testid="stake-trend-flat-note" className="text-[10px] text-muted-foreground mt-1.5 text-center">
          No change in stake over the last {chartData.length} epochs
        </div>
      ) : null}
    </div>
  );
};

// ---------- Deposit vs Total Value donut ----------
const StakeOverviewDonut = ({ depositLovelace, totalValueLovelace, amountMode }) => {
  const d = Number(depositLovelace || 0);
  const t = Number(totalValueLovelace || 0);
  const sum = d + t;
  const slices = [
    { name: "Deposit", value: d, raw: depositLovelace, color: "#facc15" },
    { name: "Total Value", value: t, raw: totalValueLovelace, color: "hsl(264 100% 68%)" },
  ];
  const pct = (v) => (sum > 0 ? ((v / sum) * 100).toFixed(2) : "0.00");

  return (
    <div className="flex flex-col items-center" data-testid="stake-overview-donut">
      <div className="relative h-[150px] w-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              innerRadius={48}
              outerRadius={68}
              paddingAngle={2}
              stroke="none"
            >
              {slices.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center">
          <span className="font-heading font-normal text-[14px] text-foreground tabnum leading-tight">
            {formatLovelace(totalValueLovelace, amountMode)}
          </span>
          <span className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">
            Total Value
          </span>
        </div>
      </div>
      <div className="w-full mt-4 space-y-1.5">
        {slices.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between text-[12px] mono gap-3">
            <span className="inline-flex items-center gap-2 shrink-0">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: entry.color }} />
              <span className="text-muted-foreground">{entry.name}</span>
            </span>
            <span className="text-foreground/90 text-right whitespace-nowrap">
              {formatLovelace(entry.raw, amountMode)}{" "}
              <span className="text-muted-foreground">({pct(entry.value)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- small stacked tile used in the right-hand column ----------
const SideTile = ({ testid, label, children, hint }) => (
  <div data-testid={testid} className="border border-primary/15 bg-card/30 rounded-xl px-4 py-3.5">
    <div className="text-[10px] uppercase tracking-[0.2em] text-primary/70 mono mb-1.5">{label}</div>
    <div className="text-[14px] text-foreground/90">{children}</div>
    {hint ? <div className="text-[10px] text-muted-foreground mt-1">{hint}</div> : null}
  </div>
);

// ---------- Accounts mode summary panel (Cardanoscan-style "Stake Key Details") ----------
export const SummaryPanel = ({ data, amountMode, onViewAddresses, onPickPool }) => {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="account-summary-panel">
      {/* header — title, status, bech32 + hex */}
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow px-6 py-5">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h2 className="font-heading font-normal text-lg md:text-xl text-primary text-glow">Stake Key Details</h2>
          <span data-testid="tile-status" className="inline-flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</span>
            {data.active ? <Badge tone="success">Active</Badge> : <Badge tone="warn">Inactive</Badge>}
          </span>
          {data.registered ? (
            <Badge tone="info">Registered</Badge>
          ) : (
            <Badge>Unregistered</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="account-bech32" className="mono text-[12px] md:text-[13px] text-primary text-glow break-all">
            {data.stake_address}
          </span>
          <CopyBtn value={data.stake_address} label="stake" />
        </div>
        {data.hex ? (
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Hex</span>
            <span data-testid="account-hex" className="mono text-[11px] text-muted-foreground break-all">
              {data.hex}
            </span>
            <CopyBtn value={data.hex} label="stake-hex" />
          </div>
        ) : null}
      </div>

      {/* two-column dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* left column: controlled stake + trend, stake overview donut */}
        <div className="space-y-4">
          <div className="border border-primary/20 bg-card/25 rounded-xl px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary/70 mono mb-1.5">
              Controlled Total Stake
            </div>
            <div className="font-heading font-normal text-2xl md:text-3xl text-foreground tabnum">
              {formatLovelace(data.controlled_amount, amountMode)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">Live UTXO stake</div>
            <div className="mt-3">
              <StakeTrendChart stakeAddress={data.stake_address} />
            </div>
          </div>

          <div className="border border-primary/20 bg-card/25 rounded-xl px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary/70 mono mb-3">
              Stake Overview
            </div>
            <StakeOverviewDonut
              depositLovelace={data.deposit}
              totalValueLovelace={data.total_value}
              amountMode={amountMode}
            />
          </div>
        </div>

        {/* right column: stacked detail tiles */}
        <div className="space-y-3">
          <SideTile testid="tile-rewards-withdrawn" label="Rewards Withdrawn">
            {formatLovelace(data.withdrawals_sum, amountMode)}
          </SideTile>
          <SideTile testid="tile-rewards-available" label="Rewards Available" hint="rewards not yet withdrawn">
            {formatLovelace(data.withdrawable_amount, amountMode)}
          </SideTile>
          <SideTile testid="tile-delegated-to" label="Delegated To">
            {data.pool_id ? (
              <span className="inline-flex items-center gap-2 flex-wrap">
                {onPickPool ? (
                  <button
                    type="button"
                    data-testid="link-delegated-pool"
                    onClick={() => onPickPool(data.pool_id)}
                    className="text-primary hover:text-[#22d3ee] hover:underline decoration-dotted underline-offset-2 transition-colors text-left break-all"
                  >
                    {data.pool_ticker
                      ? `${data.pool_name || data.pool_ticker} [ ${data.pool_ticker} ]`
                      : truncMid(data.pool_id, 14, 6)}
                  </button>
                ) : (
                  <Mono>{truncMid(data.pool_id, 14, 6)}</Mono>
                )}
                <CopyBtn value={data.pool_id} label="pool" />
              </span>
            ) : (
              <span className="text-muted-foreground italic text-[13px]">Not delegated</span>
            )}
          </SideTile>
          <SideTile testid="tile-vote-delegated-to" label="Vote Delegated To">
            {data.drep_id ? (
              <span className="inline-flex items-center gap-2">
                <Mono>{truncMid(data.drep_id, 14, 6)}</Mono>
                <CopyBtn value={data.drep_id} label="drep" />
              </span>
            ) : (
              <span className="text-muted-foreground italic text-[13px]">Not delegated</span>
            )}
          </SideTile>
          <SideTile testid="tile-deposit" label="Deposit">
            {formatLovelace(data.deposit, amountMode)}
          </SideTile>
          <SideTile testid="tile-treasury-reserves" label="Reserves / Treasury">
            <span className="flex flex-col text-[13px]">
              <span>{formatLovelace(data.reserves_sum, amountMode)}</span>
              <span className="text-muted-foreground">{formatLovelace(data.treasury_sum, amountMode)}</span>
            </span>
          </SideTile>
        </div>
      </div>

      {/* full-width call-to-action */}
      {onViewAddresses ? (
        <button
          type="button"
          data-testid="btn-view-all-addresses"
          onClick={onViewAddresses}
          className="w-full mono text-[11px] uppercase tracking-[0.2em] border border-primary/30 text-primary hover:bg-primary/10 hover:text-[#22d3ee] hover:border-[#22d3ee]/40 rounded-lg px-4 py-3 transition-colors"
        >
          View All Addresses
        </button>
      ) : null}
    </div>
  );
};

